import { useInternalNode, BaseEdge, EdgeLabelRenderer } from '@xyflow/react';
import { edgeStrokeStyle } from '../../../flowkit/flowEdge';
import { nodeIntersection } from './geometry';

export { edgeStrokeStyle } from '../../../flowkit/flowEdge';

// Lateral gap (px) between genuinely-parallel edges sharing the same vertex pair.
const PARALLEL_GAP = 20;
// How far the quadratic control point bows out per unit of parallel offset (arc amplitude).
const CURVE_FACTOR = 2.2;

// Perpendicular offset for an edge given its position among parallel edges. A lone edge (count <= 1) gets 0,
// so it stays straight; parallel edges fan out symmetrically around the centre line.
export function parallelOffset(index, count) {
  if (!count || count <= 1) return 0;
  return (index - (count - 1) / 2) * PARALLEL_GAP;
}

// Extract a plain rect ({x,y,width,height}) from a React Flow internal node.
function rectOf(internalNode) {
  const width = internalNode.measured?.width ?? 170;
  const height = internalNode.measured?.height ?? 48;
  const x = internalNode.internals.positionAbsolute?.x ?? internalNode.position.x;
  const y = internalNode.internals.positionAbsolute?.y ?? internalNode.position.y;
  return { x, y, width, height };
}

function EdgeLabel({ label, rev, x, y, selected }) {
  if (label === undefined || label === null || label === '') return null;
  return (
    <EdgeLabelRenderer>
      <div
        className={`graph-edge-label nodrag nopan${selected ? ' selected' : ''}`}
        style={{
          position: 'absolute',
          transform: `translate(-50%, -50%) translate(${x}px,${y}px)`,
          pointerEvents: 'all',
          zIndex: selected ? 10 : 1,
        }}
      >
        {label}
        {rev !== undefined && rev !== null && <span className="graph-edge-label-rev"> · r{rev}</span>}
      </div>
    </EdgeLabelRenderer>
  );
}

/**
 * GraphFloatingEdge — floating edge for arbitrary property-graph topology.
 *
 * Endpoints are clipped to each node's border (so the line touches the boxes and re-clips as nodes move).
 * A lone edge is drawn straight; genuinely-parallel edges (same vertex pair) bow apart as quadratic arcs
 * (Neo4j-style), using a canonical (sorted-id) perpendicular so opposite-direction edges arc to opposite
 * sides. The arrowhead marker (defined in canvas.js) recolours with the edge via its selected variant.
 * Self-loops draw a small arc above the node.
 */
function GraphFloatingEdge({ source, target, selected, data }) {
  const sourceNode = useInternalNode(source);
  const targetNode = useInternalNode(target);
  if (!sourceNode || !targetNode) return null;

  const stroke = edgeStrokeStyle(selected);
  const rev = data?.element?.rev;
  const label = data?.label;
  const markerEnd = `url(#graph-edge-arrow${selected ? '-selected' : ''})`;

  // Self-loop: small arc above the node.
  if (source === target) {
    const r = rectOf(sourceNode);
    const lx = r.x + r.width / 2;
    const ly = r.y;
    const rad = 20;
    const loopPath =
      `M ${lx} ${ly} C ${lx + rad} ${ly - rad * 2} ${lx + rad * 2} ${ly - rad * 2} ` +
      `${lx + rad * 2} ${ly} C ${lx + rad * 2} ${ly + rad} ${lx + rad} ${ly + rad} ${lx} ${ly}`;
    return (
      <>
        <path d={loopPath} className="react-flow__edge-path" markerEnd={markerEnd} fill="none" style={stroke} />
        <EdgeLabel label={label} rev={rev} x={lx + rad} y={ly - rad * 1.5} selected={selected} />
      </>
    );
  }

  const sRect = rectOf(sourceNode);
  const tRect = rectOf(targetNode);
  const sp = nodeIntersection(sRect, tRect);
  const tp = nodeIntersection(tRect, sRect);

  // Bow genuinely-parallel edges apart with a quadratic control point; a lone edge stays straight.
  const curve = parallelOffset(data?.parallelIndex, data?.parallelCount) * CURVE_FACTOR;
  const mx = (sp.x + tp.x) / 2;
  const my = (sp.y + tp.y) / 2;
  let cx = mx;
  let cy = my;
  if (curve !== 0) {
    const sCenter = { x: sRect.x + sRect.width / 2, y: sRect.y + sRect.height / 2 };
    const tCenter = { x: tRect.x + tRect.width / 2, y: tRect.y + tRect.height / 2 };
    const [aC, bC] = source < target ? [sCenter, tCenter] : [tCenter, sCenter];
    const dx = bC.x - aC.x;
    const dy = bC.y - aC.y;
    const len = Math.sqrt(dx * dx + dy * dy) || 1;
    cx = mx + (-dy / len) * curve;
    cy = my + (dx / len) * curve;
  }

  const edgePath = `M ${sp.x},${sp.y} Q ${cx},${cy} ${tp.x},${tp.y}`;
  // Quadratic bezier point at t=0.5 (arc apex) — keeps each parallel edge's label on its own arc.
  const labelX = 0.25 * sp.x + 0.5 * cx + 0.25 * tp.x;
  const labelY = 0.25 * sp.y + 0.5 * cy + 0.25 * tp.y;

  return (
    <>
      <BaseEdge path={edgePath} markerEnd={markerEnd} style={stroke} />
      <EdgeLabel label={label} rev={rev} x={labelX} y={labelY} selected={selected} />
    </>
  );
}

export default GraphFloatingEdge;
